import config from '@/config';
import { track } from '@shared/events';

/**
 * The one piece of the `/callback` code-exchange that was byte-identical
 * between `basic` and `magshop` (beyond the endpoint path itself): building
 * the exchange URL and firing the GET request. Everything downstream of the
 * response — `state`/redirect handling, error-shape parsing (`basic`
 * distinguishes a `USER_NOT_FOUND` code magshop doesn't), and whether to
 * `location.reload()` after — differs in ways that reflect real per-tenant
 * product behaviour, not accidental duplication, so those stay local to
 * each template's `Callback.hooks.ts` rather than being forced through a
 * shared factory. `saveTokens`/`clearTokens` (the actually-identical token
 * persistence) already moved to `services/auth/tokens.ts`.
 */
export async function exchangeCodeForTokens(
	code: string,
	redirectUri: string,
	exchangePath: string,
): Promise<Response> {
	const params = new URLSearchParams({
		code,
		redirect_uri: redirectUri,
	});

	const url = [config.build.host, exchangePath, '?', params.toString()].join(
		'',
	);

	return fetch(url, {
		method: 'GET',
		headers: {
			'app-key': config.build.appKey,
			'Content-Type': 'application/json',
		},
	});
}

/** Codes whose sign-in has been recorded: one `login` or `login_fail` each. */
const recordedCodes = new Set<string>();

/**
 * Records how a sign-in through the tenant's hosted pages ended, from the
 * `/callback` that exchanges its code. Once per code: React runs the
 * exchange twice in development, and the second, with a used code, fails.
 */
export function trackCallbackSignIn(code: string, succeeded: boolean): void {
	if (recordedCodes.has(code)) {
		return;
	}
	recordedCodes.add(code);
	// Only the Cognito flow comes back through `/callback`.
	track(succeeded ? 'login' : 'login_fail', {
		method: config.app.auth?.type ?? 'cognito',
	});
}
